import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const MobileOnly = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="h-screen w-full flex flex-col items-center justify-center bg-background p-6 text-center overflow-hidden">
      {/* 1. Icon Container - Now at the Top */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative mb-6"
      >
        <div className="absolute -inset-4 bg-primary/10 rounded-full blur-2xl animate-pulse" />
        <div className="relative flex items-center justify-center w-16 h-16 rounded-2xl bg-secondary border border-primary/15 shadow-xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
            <img 
              src="/icon.png" 
              alt="App Icon" 
              className="w-10 h-10 object-contain" 
            />
        </div>
      </motion.div>

      {/* 2. App Name */}
      <motion.h2 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="text-2xl font-bold tracking-tight bg-gradient-to-br from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent mb-8"
      >
        My Favorite Copies
      </motion.h2>

      {/* 3. Screenshot Container - Center piece */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="relative mb-10 group cursor-pointer"
        onClick={() => setIsExpanded(true)}
      >
        <div className="absolute -inset-2 bg-primary/5 rounded-xl blur-lg transition-bg duration-300 group-hover:bg-primary/10" />
        <img 
          src="/mfc_screenshot.png" 
          alt="App Screenshot" 
          className="relative w-full max-w-[240px] rounded-xl border border-border/50 shadow-2xl transition-transform duration-300 group-hover:scale-[1.02]" 
        />
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
           <div className="bg-black/40 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full font-medium">
             Touch to Expand
           </div>
        </div>
      </motion.div>
      
      {/* 4. Warning Text */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        className="space-y-3 max-w-[280px]"
      >
        <h1 className="text-lg font-semibold tracking-tight text-foreground/90">
          Desktop Only
        </h1>
        <p className="text-muted-foreground text-xs leading-relaxed">
          This application is optimized for desktop environments. Please access from a larger screen for the best experience.
        </p>
      </motion.div>
      
      {/* 5. Decorative Dots */}
      <div className="mt-12 flex gap-1.5 justify-center">
        {[...Array(3)].map((_, i) => (
          <motion.div 
            key={i} 
            animate={{ y: [0, -6, 0] }}
            transition={{ 
                repeat: Infinity, 
                duration: 1, 
                delay: i * 0.15,
                ease: "easeInOut" 
            }}
            className="w-1.5 h-1.5 rounded-full bg-primary/20" 
          />
        ))}
      </div>

      {/* Expansion Overlay */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/80 backdrop-blur-md p-4"
            onClick={() => setIsExpanded(false)}
          >
            <motion.img
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              src="/mfc_screenshot.png"
              alt="Expanded Screenshot"
              className="max-w-full max-h-full rounded-lg shadow-2xl cursor-pointer"
            />
            <div className="absolute top-6 right-6 text-white/60 text-sm font-medium">
              Tap to close
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MobileOnly;
